import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, PlugZap, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";

const errMsg = (e, fb) => formatApiErrorDetail(e?.response?.data?.detail) || fb;

export function MailConfigCard({ config, onSaved }) {
  const [form, setForm] = useState({ gmail_address: "", app_password: "", enabled: true, technician_ids: [], start_date: "" });
  const [techs, setTechs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/users", { params: { role: "tecnico,subappalto" } }).then((r) => setTechs(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (config) {
      setForm({ gmail_address: config.gmail_address || "operativo.ei@gmail.com", app_password: "", enabled: config.has_password ? config.enabled : true, technician_ids: config.technician_ids || [], start_date: config.start_date || new Date().toISOString().slice(0, 10) });
    }
  }, [config]);

  const toggleTech = (id) => setForm((f) => ({ ...f, technician_ids: f.technician_ids.includes(id) ? f.technician_ids.filter((x) => x !== id) : [...f.technician_ids, id] }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/mail-serials/config", { ...form, app_password: form.app_password || null });
      toast.success("Impostazioni salvate");
      onSaved(r.data);
    } catch (e) { toast.error(errMsg(e, "Salvataggio non riuscito")); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try { await api.post("/mail-serials/test"); toast.success("Connessione a Gmail riuscita"); }
    catch (e) { toast.error(errMsg(e, "Connessione non riuscita")); }
    finally { setTesting(false); }
  };

  return (
    <div className="rounded-sm border border-slate-200 bg-white p-6" data-testid="mail-config-card">
      <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Account Gmail</div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <label className="text-sm">
          <span className="mb-1 block text-xs font-semibold text-slate-600">Indirizzo Gmail o Google Workspace</span>
          <Input value={form.gmail_address} onChange={(e) => setForm({ ...form, gmail_address: e.target.value })} data-testid="mail-config-address-input" />
        </label>
        <label className="text-sm">
          <span className="mb-1 flex items-center gap-1 text-xs font-semibold text-slate-600"><KeyRound className="h-3 w-3" /> Password per le app (16 lettere)</span>
          <Input type="password" autoComplete="new-password" placeholder={config?.has_password ? "•••••••• salvata (lascia vuoto per non cambiarla)" : "es. abcd efgh ijkl mnop"} value={form.app_password} onChange={(e) => setForm({ ...form, app_password: e.target.value })} data-testid="mail-config-password-input" />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs font-semibold text-slate-600">Leggi le mail ricevute a partire dal</span>
          <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="mail-config-start-date" />
        </label>
        <label className="flex items-center gap-3 self-end pb-2 text-sm font-semibold text-slate-700">
          <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="mail-config-enabled-switch" />
          Lettura automatica ogni ora
        </label>
      </div>
      <div className="mt-2 text-[11px] text-slate-500">
        La password per le app si crea su myaccount.google.com → Sicurezza → Verifica in due passaggi → Password per le app (16 lettere). Non è la password normale dell'account. Per account aziendali (Google Workspace) l'amministratore deve consentire le password per le app. Viene salvata cifrata.
      </div>

      <div className="mt-6 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Tecnici di cui leggere le mail ({form.technician_ids.length})</div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="mail-config-techs">
        {techs.map((t) => (
          <label key={t.id} className={`flex cursor-pointer items-center gap-3 rounded-sm border p-3 text-sm transition-colors ${form.technician_ids.includes(t.id) ? "border-[#0052FF] bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
            <Checkbox checked={form.technician_ids.includes(t.id)} onCheckedChange={() => toggleTech(t.id)} data-testid={`mail-config-tech-${t.id}`} />
            <span className="min-w-0">
              <span className="block truncate font-semibold text-slate-800">{t.name}</span>
              <span className="block truncate text-xs text-slate-500">{t.email}</span>
            </span>
          </label>
        ))}
        {techs.length === 0 && <div className="text-sm text-slate-400">Nessun tecnico trovato</div>}
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <Button onClick={save} disabled={saving} className="rounded-sm bg-[#0052FF] hover:bg-[#0043d1]" data-testid="mail-config-save-btn">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Salva
        </Button>
        <Button variant="outline" onClick={test} disabled={testing || !config?.has_password} className="rounded-sm" data-testid="mail-config-test-btn">
          {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlugZap className="mr-2 h-4 w-4" />} Prova connessione
        </Button>
      </div>
    </div>
  );
}
